// BizDevAnimations.js - Framer Motion components for BizDev pages
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useRef, useEffect, useState, useCallback } from "react";

// ===== ANIMATION PRESETS =====
export const spring = { type: "spring", damping: 20, stiffness: 120 };
export const springBouncy = { type: "spring", damping: 12, stiffness: 150 };
export const smooth = { duration: 0.5, ease: [0.4, 0, 0.2, 1] };

// ===== ANIMATED PAGE WRAPPER =====
export function AnimatedPage({ children, className, style }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      className={className}
      style={{ ...style, position: "relative", zIndex: 1 }}
    >
      {children}
    </motion.div>
  );
}

// ===== STAGGER CONTAINER =====
export function StaggerContainer({ children, className, style, delay = 0 }) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.08, delayChildren: delay } }
      }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}

// ===== STAGGER ITEM =====
export function StaggerItem({ children, className, style, onClick, whileHover }) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 24, scale: 0.96 },
        visible: { opacity: 1, y: 0, scale: 1, transition: spring }
      }}
      whileHover={whileHover}
      className={className}
      style={style}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
}

// ===== ANIMATED CARD (hover glow + tilt) =====
export function AnimatedCard({ children, style, delay = 0, className, onClick, glow = "rgba(239,68,68,0.15)" }) {
  const ref = useRef(null);
  const handleMove = useCallback((e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    ref.current.style.transform = `perspective(800px) rotateX(${y*-6}deg) rotateY(${x*6}deg) scale(1.015)`;
  }, []);
  const handleLeave = useCallback(() => {
    if (ref.current) ref.current.style.transform = "perspective(800px) rotateX(0) rotateY(0) scale(1)";
  }, []);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ ...spring, delay }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`anim-card ${className || ""}`}
      style={{ ...style, transition: "transform 0.15s ease, box-shadow 0.3s ease" }}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
}

// ===== ANIMATED COUNTER =====
function easeOutExpo(x) { return x === 1 ? 1 : 1 - Math.pow(2, -10 * x); }

export function AnimatedCounter({ value, prefix = "", suffix = "", style, className, duration = 1.5 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    const num = parseFloat(String(value).replace(/[^0-9.]/g, "")) || 0;
    const start = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - start) / (duration * 1000), 1);
      setDisplay(num * easeOutExpo(p));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value, duration]);

  const hasDecimal = String(value).includes(".");
  const formatted = hasDecimal ? display.toFixed(1) : Math.round(display).toLocaleString();

  return (
    <motion.span
      ref={ref}
      className={`counter-value ${className || ""}`}
      style={style}
      initial={{ opacity: 0, scale: 0.5, y: 20 }}
      animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
      transition={springBouncy}
    >
      {prefix}{formatted}{suffix}
    </motion.span>
  );
}

// ===== ANIMATED ROW =====
export function AnimatedRow({ children, style, onClick, delay = 0, className }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ ...spring, delay }}
      whileHover={{ x: 4, backgroundColor: "rgba(255,255,255,0.025)", transition: { duration: 0.15 } }}
      className={className}
      style={{ ...style, cursor: onClick ? "pointer" : undefined }}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
}

// ===== ANIMATED BADGE =====
export function AnimatedBadge({ children, style, color }) {
  return (
    <motion.span
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={springBouncy}
      style={{ ...style, display: "inline-flex", position: "relative" }}
    >
      {children}
    </motion.span>
  );
}

// ===== FLOATING PARTICLES =====
export function ParticleField({ count = 15 }) {
  const particles = useRef(
    Array.from({ length: count }, () => ({
      x: Math.random() * 100 + "%",
      size: 2 + Math.random() * 5 + "px",
      dur: 4 + Math.random() * 8 + "s",
      delay: Math.random() * 6 + "s",
    }))
  ).current;
  return (
    <div className="particle-bg">
      {particles.map((p, i) => (
        <div key={i} className="particle" style={{ "--x": p.x, "--size": p.size, "--dur": p.dur, "--delay": p.delay }} />
      ))}
    </div>
  );
}

// ===== ANIMATED SECTION HEADER =====
export function SectionHeader({ children, style }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={smooth}
      className="shimmer-text"
      style={style}
    >
      {children}
    </motion.div>
  );
}

// ===== PROGRESS BAR =====
export function AnimatedProgress({ value, max, color, style, delay = 0 }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ width: "100%", height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden", ...style }}>
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: pct + "%" }}
        transition={{ duration: 1.2, delay, ease: [0.4, 0, 0.2, 1] }}
        style={{ height: "100%", background: color || "#ef4444", borderRadius: 3 }}
      />
    </div>
  );
}

// ===== HOVER SCALE WRAPPER =====
export function HoverScale({ children, scale = 1.04, className, style, onClick }) {
  return (
    <motion.div
      whileHover={{ scale }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.15 }}
      className={className}
      style={style}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
}
